import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParameterCodec,
  HttpParams as NgHttpParams,
  HttpUrlEncodingCodec as NgHttpUrlEncodingCodec
} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, pluck, switchMap, tap} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {blobToString, cache} from '../../utils';
import {AnalyticsService} from './analytics.service';
import {AuthService, SpringOAuthResponse, TokenResponse} from './auth.service';

export interface IdentificationResponse<T> {
  needsIdentification: boolean;
  username: string;
  isCollaborator: boolean;
  response: T;
}

export interface QccFeedbackData {
  reason: string;
  cadence: string;
}

export type IdentificationOrError<T> = IdentificationResponse<T> | ErrorResponse;
export type OptionalIdentification<T> = IdentificationOrError<T> | T;
export type IdentificationObservable<T> = Observable<OptionalIdentification<T>>;

export function isIdentificationResponse<T>(obj: any): obj is IdentificationResponse<T> {
  return typeof obj === 'object' && obj !== null && 'needsIdentification' in obj;
}

// FIXME workaround for https://github.com/angular/angular/issues/11058
export class HttpUrlEncodingCodec extends NgHttpUrlEncodingCodec {
  encodeKey(k: string): string {
    return super.encodeKey(k).replace(/\+/gi, '%2B');
  }

  encodeValue(v: string): string {
    return super.encodeValue(v).replace(/\+/gi, '%2B');
  }
}

export class HttpParams extends NgHttpParams {
  constructor(options: {
    fromString?: string,
    encoder?: HttpParameterCodec,
  } = {}) {
    super({
      fromString: options.fromString,
      encoder: options.encoder || new HttpUrlEncodingCodec()
    });
  }
}

interface HttpOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  url: string;
  params?: HttpParams;
  authenticated?: boolean;
  data?: any;
  withCredentials?: boolean;
  headers?: HttpHeaders;
  responseType?: 'text' | 'json' | 'blob';
  redirectLogin?: boolean; // Redirect to login on 401 (default: true)
  reportProgress?: boolean;
  fullResponse?: boolean;
}

export interface HttpError {
  error: HttpErrorResponse;
  status: number;
  code?: string;
  data?: any;
}

export interface ErrorResponse {
  error: string;
  errorId: string;
  errorDescription: string;
}

export function isErrorResponse(obj: any): obj is ErrorResponse {
  return typeof obj === 'object' && obj !== null && 'error' in obj;
}

export interface SuccessResponse {
  success: string;
}

@Injectable()
export class ApiService {

  constructor(private http: HttpClient,
              private Auth: AuthService,
              private Analytics: AnalyticsService) {
  }

  refreshUser(): Observable<User | null> {
    // this.clearCache();
    if (this.Auth.hasAuthentication()) {
      this.Auth.startRefresh();

      const tempUser = this.Auth.getAuthType() === 'timecard';
      return this.requestAuthToken()
        .pipe(
          switchMap(() => this.getUserInfo(tempUser)),
          map((data: User) => {
            this.Auth.finishRefresh();
            this.Analytics.identify(data);
            return data;
          }),
          catchError((err: HttpError) => {
            console.error('Failed to refresh authentication', err);
            this.Analytics.trackEvent('User information retrieval failed', {category: 'Error'});
            window.location.href = `${environment.matHost}/myaquent?PROC=AWUIDrawMatLogin`;
            return of(null);
          })
        );
    } else {
      this.Auth.removeAuthentication();
      return of(null);
    }
  }

  authenticate(username: string, password: string): Observable<SpringOAuthResponse> {
    return this.httpWrapper<SpringOAuthResponse>({
      method: 'POST',
      url: `${environment.apiHost}/oauth/token`,
      data: new HttpParams()
        .set('client_id', 'order-container')
        .set('grant_type', 'password')
        .set('username', username)
        .set('password', password)
        .set('persistent', 'true')
    }).pipe(map((data): SpringOAuthResponse => {
      this.Auth.storeOAuthAuthentication(data);
      return data;
    }));
  }

  authenticateAs(authToken: string): Observable<SpringOAuthResponse> {
    const formData = new FormData();
    formData.append('client_id', 'login-as');
    formData.append('grant_type', 'password');
    formData.append('login-as', authToken);

    return this.httpWrapper<SpringOAuthResponse>({
      method: 'POST',
      url: `${environment.apiHost}/oauth/token`,
      data: formData
    }).pipe(map((data): SpringOAuthResponse => {
      this.Auth.storeOAuthAuthenticationSession(data, true);
      return data;
    }));
  }

  convertToken(authToken: TokenResponse): Observable<SpringOAuthResponse> {
    this.Auth.storeTokenAuthentication(authToken);
    return this.httpWrapper<SpringOAuthResponse>({
      method: 'POST',
      url: `${environment.apiHost}/v1/register/convert/token`,
      data: authToken.token,
      authenticated: true
    }).pipe(
      map((data): SpringOAuthResponse => {
        if (data) {
          this.Auth.storeOAuthAuthenticationSession(data, false);
        }
        return data;
      })
    );
  }

  requestAuthToken(): Observable<void> {
    if (this.Auth.getAuthSourceUrl()) {
      return this.httpWrapper<TokenResponse>({
        method: 'GET',
        url: this.Auth.getAuthSourceUrl(),
        withCredentials: true,
        authenticated: true
      }).pipe(
        switchMap(token =>
          this.convertToken(token)),
        map(() => {
          return null;
        })
      );
    } else {
      return of(null);
    }
  }

  getUserInfo(tempUser: boolean): Observable<User> {
    return this.httpWrapper<User>({
      method: 'GET',
      url: `${environment.apiHost}/v1/me`,
      authenticated: true,
      redirectLogin: false
    }).pipe(
      tap(user => {
        if (tempUser) {
          this.Auth.temporaryUser$.next(user);
        } else {
          this.Auth.user$.next(user);
        }
      }),
      cache()
    );
  }

  getUserProfileInfo(): Observable<PersonProfileInformation> {
    return this.httpWrapper<PersonProfileInformation>({
      method: 'GET',
      url: `${environment.apiHost}/v1/profile/information`,
      authenticated: true
    });
  }

  getUserCredentialInfo(): Observable<PersonCredentialInformation> {
    return this.httpWrapper<PersonCredentialInformation>({
      method: 'GET',
      url: `${environment.apiHost}/v1/credential/information`,
      authenticated: true
    });
  }

  getProfileAvatar(): Observable<any> {
    return this.httpWrapper<String>({
      method: 'GET',
      url: `${environment.apiHost}/v1/profile/avatar`,
      authenticated: true
    });
  }

  getCountriesList(): Observable<CountryListDropdownResponse> {
    return this.httpWrapper<CountryListDropdownResponse>({
      method: 'GET',
      url: `${environment.apiHost}/v1/reference/countries`,
      authenticated: true
    });
  }

  updateProfileInformation(formData): Observable<any> {
    return this.httpWrapper({
      method: 'PUT',
      url: `${environment.apiHost}/v1/profile/information`,
      data: formData,
      authenticated: true
    });
  }

  updateCredentialInformation(formData): Observable<any> {
    return this.httpWrapper({
      method: 'PUT',
      url: `${environment.apiHost}/v1/credential/information`,
      data: formData,
      authenticated: true
    });
  }

  updateProfileAvatar(formData): Observable<any> {
    return this.httpWrapper({
      method: 'PUT',
      url: `${environment.apiHost}/v1/profile/avatar`,
      data: formData,
      authenticated: true
    });
  }

  deleteProfileAvatar(): Observable<any> {
    return this.httpWrapper({
      method: 'DELETE',
      url: `${environment.apiHost}/v1/profile/avatar`,
      authenticated: true
    });
  }

  getLocaleList(): Observable<LocaleListDropdownResponse> {
    return this.httpWrapper<LocaleListDropdownResponse>({
      method: 'GET',
      url: `${environment.apiHost}/v1/reference/locales`,
      authenticated: true
    });
  }

  getDisplaySettings(): Observable<DisplaySettings> {
    return this.httpWrapper<DisplaySettings>({
      method: 'GET',
      url: `${environment.apiHost}/v1/settings/display`,
      authenticated: true
    });
  }

  updateDisplaySettings(displaySettings: DisplaySettings): Observable<any> {
    return this.httpWrapper({
      method: 'PUT',
      url: `${environment.apiHost}/v1/settings/display`,
      data: displaySettings,
      authenticated: true
    });
  }

  getInvoiceSettings(): Observable<InvoiceSettings> {
    return this.httpWrapper<InvoiceSettings>({
      method: 'GET',
      url: `${environment.apiHost}/v1/settings/invoice`,
      authenticated: true
    });
  }

  updateInvoiceSettings(invoiceSettings: InvoiceSettings): Observable<any> {
    return this.httpWrapper({
      method: 'PUT',
      url: `${environment.apiHost}/v1/settings/invoice`,
      data: invoiceSettings,
      authenticated: true
    });
  }

  getInvoiceRecipients(): Observable<InvoiceRecipients> {
    return this.httpWrapper<InvoiceRecipients>({
      method: 'GET',
      url: `${environment.apiHost}/v1/settings/invoice/recipients`,
      authenticated: true
    });
  }

  updateInvoiceRecipients(invoiceRecipients: InvoiceRecipients): Observable<any> {
    return this.httpWrapper({
      method: 'PUT',
      url: `${environment.apiHost}/v1/settings/invoice/recipients`,
      data: invoiceRecipients,
      authenticated: true
    });
  }

  getTimecardSettings(): Observable<TimecardSettings> {
    return this.httpWrapper<TimecardSettings>({
      method: 'GET',
      url: `${environment.apiHost}/v1/settings/timecard`,
      authenticated: true
    });
  }

  updateTimecardSettings(timecardSettings: TimecardSettings): Observable<any> {
    return this.httpWrapper({
      method: 'PUT',
      url: `${environment.apiHost}/v1/settings/timecard`,
      data: timecardSettings,
      authenticated: true
    });
  }

  getPossibleAlternateSigners(): Observable<AlternateSigners> {
    return this.httpWrapper<AlternateSigners>({
      method: 'GET',
      url: `${environment.apiHost}/v1/settings/timecard/possible-signers`,
      authenticated: true
    });
  }

  getAlternateSigners(): Observable<AlternateSigners> {
    return this.httpWrapper<AlternateSigners>({
      method: 'GET',
      url: `${environment.apiHost}/v1/settings/timecard/alternate-signers`,
      authenticated: true
    });
  }

  updateAlternateSigners(alternateSignerIds: AlternateSignerIds): Observable<any> {
    return this.httpWrapper({
      method: 'PUT',
      url: `${environment.apiHost}/v1/settings/timecard/alternate-signers`,
      data: alternateSignerIds,
      authenticated: true
    });
  }

  initRegistration(email: string, password: string, orderId?: string | null): Observable<any> {
    return this.httpWrapper({ // TODO type
      method: 'POST',
      url: `${environment.apiHost}/v1/register`,
      data: new HttpParams()
        .set('email', email)
        .set('password', password)
        .set('orderId', orderId)
    });
  }

  forgotPassword(username: string): Observable<any> {
    return this.httpWrapper<any>({ // TODO type?
      method: 'POST',
      url: `${environment.apiHost}/v1/account/password/forgot`,
      data: new HttpParams().set('username', username)
    });
  }

  resetPassword(code: string, password: string): Observable<any> {
    return this.httpWrapper<any>({ // TODO type?
      method: 'POST',
      url: `${environment.apiHost}/v1/account/password/reset`,
      data: new HttpParams()
        .set('code', code)
        .set('password', password)
    });
  }

  resendVerificationEmail(username: string, password: string): Observable<SuccessResponse> {
    return this.httpWrapper<SuccessResponse>({
      method: 'POST',
      url: `${environment.apiHost}/v1/register/resend`,
      data: new HttpParams()
        .set('username', username)
        .set('password', password)
    });
  }

  resendVerificationEmailFromCode(code: string): Observable<SuccessResponse> {
    return this.httpWrapper<SuccessResponse>({
      method: 'POST',
      url: `${environment.apiHost}/v1/register/resend-verification`,
      data: new HttpParams().set('code', code)
    });
  }

  resendAccountInvite(code: string): Observable<SuccessResponse> {
    return this.httpWrapper<SuccessResponse>({
      method: 'POST',
      url: `${environment.apiHost}/v1/register/resend-invitation`,
      data: new HttpParams().set('code', code)
    });
  }

  readUsernameFromCode(code: string): Observable<string> {
    return this.httpWrapper<any>({
      method: 'GET',
      url: `${environment.apiHost}/v1/register/username`,
      params: new HttpParams().set('code', code)
    }).pipe(pluck('username'));
  }

  completeRegistration(code: string, password: string): Observable<SpringOAuthResponse> {
    return this.httpWrapper<SpringOAuthResponse>({
      method: 'POST',
      url: `${environment.apiHost}/v1/register/complete`,
      params: new HttpParams()
        .set('code', code)
        .set('password', password)
    }).pipe(tap(data => this.Auth.storeOAuthAuthentication(data)));
  }

  checkRegistration(code: string): Observable<SpringOAuthResponse> {
    return this.httpWrapper<SpringOAuthResponse>({
      method: 'GET',
      url: `${environment.apiHost}/v1/register/check`,
      params: new HttpParams().set('code', code)
    }).pipe(tap(data => this.Auth.storeOAuthAuthentication(data)));
  }

  getTalentResumeLink(submittalProfileId: string): string {
    return `${environment.apiHost}/v1/toolkit/candidate/${submittalProfileId}/resume`;
  }

  saveProfile(profileId: string, content): Observable<void> {
    return this.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/toolkit/candidate/${profileId}/profile`,
      data: content,
      authenticated: true
    });
  }

  replaceResume(submittalProfileId: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this.httpWrapper<any>({
      method: 'POST',
      url: `${environment.apiHost}/v1/toolkit/candidate/${submittalProfileId}/resume`,
      data: formData,
      authenticated: true,
      reportProgress: true
    });
  }

  sendFeedback(message: string): Observable<void> {
    return this.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/toolkit/feedback`,
      data: new HttpParams().set('message', message),
      authenticated: true
    });
  }

  identifyWithOrder(email: string, orderId?: string | null): IdentificationObservable<void> {
    let data = new HttpParams().set('email', email);
    if (orderId) data = data.set('orderId', orderId);
    return this.httpWrapper<IdentificationOrError<void>>({
      method: 'POST',
      url: `${environment.apiHost}/v1/toolkit/identify`,
      data
    });
  }

  updateTutorialAndDiversityTutorialSeenStatus(personId: number, tutorialSeen: boolean, diversityTutorialSeen: boolean): Observable<void> {
    return this.httpWrapper<void>({
      method: 'POST',
      url: `${environment.apiHost}/v1/tutorial/${personId}`,
      data: new HttpParams().set('tutorialSeen', tutorialSeen.toString()).set('diversityTutorialSeen', diversityTutorialSeen.toString()),
      authenticated: true
    });
  }

  saveQccFeedback(emailTrackingId: string, data: QccFeedbackData): Observable<SuccessResponse> {
    return this.httpWrapper<SuccessResponse>({
      method: 'POST',
      url: `${environment.apiHost}/v1/qcc/${emailTrackingId}/feedback`,
      data
    });
  }

  httpWrapper<T>(opts: HttpOptions): Observable<T> {
    let headers = opts.headers || new HttpHeaders();
    if (opts.authenticated) {
      const authHeaders = this.Auth.getHeaders();
      for (const header in authHeaders) {
        if (authHeaders.hasOwnProperty(header)) {
          headers = headers.set(header, authHeaders[header]);
        }
      }
    }
    if (opts.responseType === 'blob') {
      headers.set('Accept', '*/*');
    }
    const responseType = (!opts.responseType || opts.responseType === 'json')
      ? 'text' // FIXME why doesn't 'json' work?
      : opts.responseType;
    return this.http.request(opts.method, opts.url, {
        observe: opts.fullResponse ? 'response' : (opts.reportProgress ? 'events' : 'body'),
        responseType,
        headers,
        params: opts.params,
        body: opts.data,
        withCredentials: opts.withCredentials,
        reportProgress: opts.reportProgress
      })
      .pipe(
        map(data => {
          if (!opts.reportProgress && (!opts.responseType || opts.responseType === 'json')) {
            return data ? JSON.parse(data) : null;
          }
          return data;
        }),
        catchError(async (err: HttpErrorResponse) => {
          let data: any = err.error;
          try {
            if (data instanceof Blob) {
              data = await blobToString(data);
            }
            data = JSON.parse(data);
          } catch (e) {
          }
          // Ignore 401 handling if opts.authenticated is truthy, but not true
          if (opts.authenticated && err.status === 401) {
            this.Auth.removeAuthentication();
          }
          throw <HttpError> {
            error: err,
            status: err.status,
            code: data && (data.errorId || data.error),
            data
          };
        }),
        cache()
      );
  }
}
